import { DatesSetArg } from '@fullcalendar/core';
import FullCalendar from '@fullcalendar/react';
import { Api } from '@monorepo/shared/apiClient';
import { Loading } from '@monorepo/shared/componentsV2/Loading';
import { useCurrentProject } from '@monorepo/shared/hooks/useCurrentProject';
import { useSaveUserSettings } from '@monorepo/shared/hooks/useSaveUserSettings';
import { useUserSettings } from '@monorepo/shared/hooks/useUserSettings';
import { UserSettingsKey } from 'mapistry-shared';
import React, {
  createContext,
  RefObject,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import {
  DEFAULT_COMPLIANCE_CALENDAR_FILTERS,
  DEFAULT_COMPLIANCE_CALENDAR_VIEW,
} from '../../consts';
import {
  CalendarViewType,
  resolveCalendarViewType,
} from './helpers/complianceCalendarHelpers';
import { useComplianceCalendarNavigation } from './hooks/useComplianceCalendarNavigation';

type ComplianceCalendarContextValue = {
  calendarRef: RefObject<FullCalendar>;
  filters: Api.ComplianceCalendarFilters;
  setFilters: (filters: Api.ComplianceCalendarFilters) => void;
  goToNext: () => void;
  goToNextYear: () => void;
  goToPrevious: () => void;
  goToPreviousYear: () => void;
  goToToday: () => void;
  handleViewChange: (view: CalendarViewType) => void;
  onDatesSet: (info: DatesSetArg) => void;
  title: string;
  updateVisibleRange: (start: Date, exclusiveEnd: Date) => void;
  view: CalendarViewType;
  visibleRangeExclusiveEnd: Date;
  visibleRangeStart: Date;
};

const ComplianceCalendarContext = createContext<
  ComplianceCalendarContextValue | undefined
>(undefined);

type ComplianceCalendarReadyProviderProps = {
  children: React.ReactNode;
  filters: Api.ComplianceCalendarFilters;
  setFilters: (filters: Api.ComplianceCalendarFilters) => void;
  setView: (view: CalendarViewType) => void;
  view: CalendarViewType;
};

function ComplianceCalendarReadyProvider({
  children,
  filters,
  setFilters,
  setView,
  view,
}: ComplianceCalendarReadyProviderProps) {
  const navigation = useComplianceCalendarNavigation({ view, setView });

  const value = useMemo(
    () => ({ ...navigation, filters, setFilters }),
    [navigation, filters, setFilters],
  );

  return (
    <ComplianceCalendarContext.Provider value={value}>
      {children}
    </ComplianceCalendarContext.Provider>
  );
}

export function ComplianceCalendarProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { project } = useCurrentProject();

  const { settings: filters } = useUserSettings({
    projectId: project?.id,
    key: UserSettingsKey.COMPLIANCE_CALENDAR_FILTERS,
    defaultValue: DEFAULT_COMPLIANCE_CALENDAR_FILTERS,
    errorMessage: 'Unable to load calendar filters',
  });
  const setFilters = useSaveUserSettings({
    projectId: project?.id,
    key: UserSettingsKey.COMPLIANCE_CALENDAR_FILTERS,
    debounceMs: 500,
    errorMessage: 'Unable to save calendar filters.',
  });

  const { settings: viewSettings, isLoading: isViewSettingsLoading } =
    useUserSettings({
      projectId: project?.id,
      key: UserSettingsKey.COMPLIANCE_CALENDAR_VIEW,
      defaultValue: DEFAULT_COMPLIANCE_CALENDAR_VIEW,
      errorMessage: 'Unable to load calendar view',
    });
  const saveViewSettings = useSaveUserSettings({
    projectId: project?.id,
    key: UserSettingsKey.COMPLIANCE_CALENDAR_VIEW,
    errorMessage: 'Unable to save calendar view.',
  });

  const view = resolveCalendarViewType(viewSettings.view);
  const setView = useCallback(
    (nextView: CalendarViewType) => {
      saveViewSettings({ view: nextView });
    },
    [saveViewSettings],
  );

  if (isViewSettingsLoading) {
    return <Loading />;
  }

  return (
    <ComplianceCalendarReadyProvider
      filters={filters}
      setFilters={setFilters}
      setView={setView}
      view={view}
    >
      {children}
    </ComplianceCalendarReadyProvider>
  );
}

export function useComplianceCalendarContext(): ComplianceCalendarContextValue {
  const context = useContext(ComplianceCalendarContext);

  if (!context) {
    throw new Error(
      'useComplianceCalendarContext must be used within ComplianceCalendarProvider',
    );
  }

  return context;
}
